import {getTestProps} from '@benchling/js-test-utils';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS, ActionOrigin} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import notifications from 'notifications';
import {TaskSchemaNodeSection} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  isInteractiveTaskNode,
  TaskSchemaNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {useAllSchemasById} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {
  FormChangeBanner,
  ProcessSchemaErrorList,
  ProcessSchemaFieldTable,
} from 'processes/settings/ProcessTaskSchemaEditor';
import {
  createOrUpdateSnapshotField,
  FieldsKey,
  ProcessTaskSchema,
  UpdateProcessSchema,
  updateProcessTaskSchema,
  updateProcessTaskSchemaForTaskDefinitionVersion,
} from 'processes/settings/ProcessTaskSchemaUtils';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {getNewOrUpdatedSnapshotFields, haveFieldsChanged} from 'registry/field/FieldUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {cacheUpdatedTaskSchemaAndUpdateNode, NodeSidepanelBaseData} from './nodeUtils';

export interface InputOutputFieldTablesProps extends NodeSidepanelBaseData {
  onChangeSelectedFieldId: (fieldId: string | null) => void;
  locked?: boolean;
}

export default function InputOutputFieldTables(props: InputOutputFieldTablesProps): JSX.Element {
  const context = NodeDetailsSidepanelContext.useContext();
  const {selectedNode} = props;
  const {storableSchemas, resultSchemas, haveSchemasLoaded} = useAllSchemasById(props.flowchartTaskSchema);

  assert(isInteractiveTaskNode(selectedNode), 'Expected selected node to be a task node');
  const taskNodeData = selectedNode.data;
  const taskSchemaId = taskNodeData.taskSchemaId;
  const baseTaskSchema = props.allSchemasById[taskSchemaId] as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  const taskSchema: ProcessTaskSchema = {
    ...baseTaskSchema,
    relevantForms: [],
    flowchartConfig: null,
  };

  const taskDefinition = context.taskDefinitionsForSidepanel.find(
    (def) => def.latestVersion?.processTaskSchema?.id === taskSchemaId
  );
  const taskDefinitionId = taskDefinition?.id;

  const hasOutputSchema = taskSchema?.outputSchema != null;
  const isFlowchartTaskSchema = props.flowchartTaskSchema.id === taskSchemaId;
  const isRootNode = taskNodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;
  const shouldShowInputFields = !isFlowchartTaskSchema || isRootNode;
  const shouldShowOutputFields = (!isFlowchartTaskSchema || !isRootNode) && hasOutputSchema;
  const validRelevantForms = [];

  const [errors, setErrors] = useState<{[errorKey: string]: string}>({});

  if (!haveSchemasLoaded) {
    return <CenteredHorizontalThrobber />;
  }

  function viewMappingsColumn(fieldRow: string, field: SchemaField): JSX.Element {
    return (
      <Button
        buttonStyle={ButtonStyle.LINK}
        text="View mappings"
        onClick={() => {
          props.onChangeSelectedFieldId(field.apiId);
        }}
      />
    );
  }

  async function updateFields(
    oldFields: Array<ClientSchemaField>,
    updatedFields: Array<ClientSchemaField>,
    fieldsKey: FieldsKey,
    setStateOnUpdate?: (params: UpdateProcessSchema & {updateTables?: boolean}) => void
  ): Promise<void> {
    let fieldsChanged;
    try {
      fieldsChanged = haveFieldsChanged(oldFields, updatedFields);
    } catch (e) {
      if (e instanceof FieldNamingException) {
        notifications.error(e.message);
        return;
      } else {
        throw e;
      }
    }

    if (!fieldsChanged) {
      return;
    }

    const newOrUpdatedSnapshotFields = getNewOrUpdatedSnapshotFields(oldFields, updatedFields);
    if (newOrUpdatedSnapshotFields.length > 0) {
      await createOrUpdateSnapshotField(
        newOrUpdatedSnapshotFields,
        updatedFields,
        fieldsKey,
        setErrors,
        undefined,
        props.refetchFlowchartConfig,
        taskSchema
      );
    } else if (taskDefinitionId != null) {
      const taskDefinitionVersion = await updateProcessTaskSchemaForTaskDefinitionVersion(
        {
          ...{[fieldsKey]: updatedFields},
        },
        taskDefinitionId,
        () => {},
        setErrors,
        taskSchema,
        hasOutputSchema
      );
      if (taskDefinitionVersion != null && !isInaccessible(taskDefinitionVersion.processTaskSchema)) {
        cacheUpdatedTaskSchemaAndUpdateNode(
          taskDefinitionVersion.processTaskSchema as ProcessTaskSchema,
          selectedNode as TaskSchemaNode,
          context.onUpdateTaskDefinitionVersion,
          taskDefinitionVersion
        );
      }
    } else {
      const taskSchemaResult = await updateProcessTaskSchema(
        {
          ...{[fieldsKey]: updatedFields},
        },
        () => {},
        setErrors,
        taskSchema,
        hasOutputSchema
      );
      if (taskSchemaResult != null) {
        cacheUpdatedTaskSchemaAndUpdateNode(
          taskSchemaResult,
          selectedNode as TaskSchemaNode,
          context.onUpdateTaskDefinitionVersion,
          null
        );
      }
    }

    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.EDITED_TASK_NODE_DEFINITION_FROM_FLOWCHART, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      updatedInputAndOutputFields: true,
      taskDefinitionVersionId: taskDefinition?.latestVersion?.id,
      taskSchemaId: taskSchema.id,
      executionType: taskSchema.executionType,
      flowchartType: context.flowchartEditorConfigurator.flowchartTypeName,
    });
  }

  return (
    <div {...getTestProps({component: 'InputOutputFieldTables', element: 'root'})}>
      {shouldShowInputFields && (
        <SectionWrapper headerName="Input fields" elementName="input-fields">
          <ProcessSchemaErrorList error={errors.taskFields ?? ''} />
          <ProcessSchemaFieldTable
            isDisabled={props.locked}
            key={`task-field-table-${taskSchemaId}`}
            hasBottomLink={!hasOutputSchema}
            schema={taskSchema}
            ownerUrlId={taskSchema.organization.url_identifier}
            ownerId={taskSchema.organization.id}
            onFieldsChange={(updatedTaskFields) => {
              updateFields(taskSchema.allFields, updatedTaskFields, 'taskFields');
            }}
            schemaType="task"
            storableSchemas={storableSchemas}
            fields={taskSchema.allFields}
            resultSchemas={resultSchemas}
            dataTestAttrs={{component: 'InputOutputFieldTables', element: 'taskTable'}}
            changeWarnings={
              validRelevantForms.length > 0
                ? [<FormChangeBanner numRelevantForms={validRelevantForms.length} />]
                : undefined
            }
            fieldToAdditionalColumn={(field) => viewMappingsColumn(TaskSchemaNodeSection.INPUTS, field)}
          />
        </SectionWrapper>
      )}
      {shouldShowOutputFields && (
        <SectionWrapper headerName="Output fields" elementName="output-fields">
          <ProcessSchemaErrorList error={errors.outputFields ?? ''} />
          <ProcessSchemaFieldTable
            isDisabled={props.locked}
            key={`output-field-table-${taskSchemaId}`}
            hasBottomLink={true}
            schema={taskSchema.outputSchema}
            ownerId={taskSchema.organization.id}
            ownerUrlId={taskSchema.organization.url_identifier}
            onFieldsChange={(updatedOutputFields) => {
              updateFields(taskSchema.outputSchema!.allFields, updatedOutputFields, 'outputFields');
            }}
            schemaType="output"
            storableSchemas={storableSchemas}
            fields={taskSchema.outputSchema!.allFields}
            resultSchemas={resultSchemas}
            dataTestAttrs={{component: 'InputOutputFieldTables', element: 'outputTable'}}
            fieldToAdditionalColumn={(field) => viewMappingsColumn(TaskSchemaNodeSection.OUTPUTS, field)}
          />
        </SectionWrapper>
      )}
    </div>
  );
}
